import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Modal from 'src/components/customModal';
import { styledMainTitle } from 'src/components/InviteModal/styles';
import modals from 'src/constants/modals';
import { ActionButtons } from 'src/layouts/main/styles';
import {
	getTransactionOverviewThunk,
	selectTransactionOverview,
	selectTransactionParticipants,
} from 'src/redux/slices/transaction';
import { dispatch } from 'src/redux/store';
import { handleCloseModal } from 'src/utils/propertyHelpers';
import { RHFRadioGroup } from 'src/components/hook-form';
import syncTransactionTasks from 'src/api/transaction/sync-transaction-tasks';
import confirmParticipantChangedName from 'src/api/transaction/confirm-participant-changed-name';
import { ConfirmParticipantChangedNameForm } from './types';
import { validationSchema } from './validation';

const Content = () => {
	const { enqueueSnackbar } = useSnackbar();
	const transaction = useSelector(selectTransactionOverview);
	const participants = useSelector(selectTransactionParticipants);
	const [isLoading, setIsLoading] = useState(false);
	const form = useForm<ConfirmParticipantChangedNameForm>({
		resolver: yupResolver(validationSchema),
		defaultValues: {},
	});

	const handleSubmit = async (data: ConfirmParticipantChangedNameForm) => {
		try {
			setIsLoading(true);

			await confirmParticipantChangedName({ id: transaction.id, placeholderId: data.placeholderId });
			await syncTransactionTasks(transaction.id);

			await dispatch(getTransactionOverviewThunk({ id: transaction.id }));

			handleCloseModal();
		} catch (e) {
			if (e instanceof Error) enqueueSnackbar(e.message, { variant: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const proprietors = participants.sellers.filter((p) => p.relationship === 'proprietor' && p.isPlaceholder);

	return (
		<FormProvider {...form}>
			<form
				onSubmit={(e) => {
					e.stopPropagation();
					form.handleSubmit(handleSubmit)(e);
				}}
			>
				<Typography sx={styledMainTitle} pt={0}>
					LET US KNOW WHO YOU ARE
				</Typography>
				<RHFRadioGroup
					name='placeholderId'
					row={false}
					sx={{ width: '100%' }}
					options={proprietors.map((p) => ({
						key: p.uid,
						name: (
							<b>
								{p.givenNames} {p.lastNames}
							</b>
						),
					}))}
				/>
				<ActionButtons mt={'20px'}>
					<LoadingButton variant='outlined' size='large' fullWidth onClick={handleCloseModal} loading={isLoading}>
						Cancel
					</LoadingButton>
					<LoadingButton variant='contained' size='large' fullWidth type='submit' loading={isLoading}>
						Submit
					</LoadingButton>
				</ActionButtons>
			</form>
		</FormProvider>
	);
};

const ConfirmParticipantChangedNameModal = () => (
	<Modal name={modals.confirmParticipantChangedName} cardSx={{ overflow: 'visible', maxWidth: '550px' }}>
		<Content />
	</Modal>
);

export default ConfirmParticipantChangedNameModal;
